import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, ChevronRight, LogOut, Plus } from 'lucide-react';
import { LOCALES, LOCALE_LABELS, isLocale } from '@worki/shared';
import { changeLocale } from '../../i18n.js';
import { api } from '../../lib/api.js';
import { usePersistedState } from '../../hooks/usePersistedState.js';
import { AnchoredMenu } from '../AnchoredMenu.js';
import { WorkiLogo } from '../WorkiLogo.js';
import { Select } from '../ui.js';
import { cn } from '../../lib/cn.js';
import { SECTIONS } from '../../pages/platform/sections.js';
import {
  CREATE_ACTIONS,
  PLATFORM_NAV,
  PRIMARY_NAV,
  PRIMARY_NAV_SPLIT,
  isActivePath,
  type ShellNavItem,
} from './navItems.js';

interface DesktopSidebarProps {
  onLogout: () => void;
}

/**
 * The rail reads `PRIMARY_NAV` itself rather than being handed a list, for the
 * same reason `BottomNav` does: the two are meant to show the same four
 * destinations, and a list passed in from the shell is a list that can quietly
 * grow on one of them and not the other.
 */
export function DesktopSidebar({ onLogout }: DesktopSidebarProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = usePersistedState('worki_sidebar_collapsed', false);
  /*
   * ADM — the back office is eleven screens, and on a desktop they belong in
   * the rail rather than behind a home screen of cards: an employee working
   * through approvals should reach packages without going back out first.
   *
   * The product's own rail does not disappear to make room for them, because
   * this is the same person's account and they leave the admin through it.
   * It folds instead, under a triangle that remembers which way it was left —
   * open on the way in, and shut for as long as somebody is working here.
   */
  const inPlatform = location.pathname.startsWith('/platform');
  const [frontOpen, setFrontOpen] = usePersistedState('worki_sidebar_front_open', true);
  const [openPlus, setOpenPlus] = useState(false);
  const plusRef = useRef<HTMLButtonElement>(null);

  /* Whether this session is Worki staff, asked by the one route in module 13
     that answers everybody — the rest reply 404 to a customer, which a menu
     cannot tell apart from the API being down. The answer cannot change without
     a new session, so it is fetched once and never refetched, and the account
     screen shares the cache under the same key. */
  const platform = useQuery({
    queryKey: ['platform', 'me'],
    queryFn: () => api.get<{ isPlatformAdmin: boolean }>('/platform/me'),
    staleTime: Infinity,
  });

  const row = (item: ShellNavItem) => {
    const isActive = isActivePath(location.pathname, item);
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
          isActive
            ? 'bg-sidebar-accent text-sidebar-primary font-medium'
            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
        )}
        title={collapsed ? t(item.labelKey) : undefined}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!collapsed && <span className="text-sm">{t(item.labelKey)}</span>}
      </Link>
    );
  };

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col h-dvh sticky top-0 bg-sidebar text-sidebar-foreground border-e border-sidebar-border transition-all duration-300',
        collapsed ? 'w-[72px]' : 'w-[240px]',
      )}
    >
      <div className="flex items-center justify-between h-16 px-4 border-b border-sidebar-border">
        {!collapsed && (
          <Link to="/" className="text-lg font-bold text-sidebar-primary hover:opacity-80 transition-opacity">
            <WorkiLogo className="h-7 w-auto" title={t('common.appName')} />
          </Link>
        )}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={t(collapsed ? 'shell.expand' : 'shell.collapse')}
          className="p-1.5 rounded-md hover:bg-sidebar-accent transition-colors"
        >
          {/* The glyph points at the edge the rail moves towards, which swaps
              with the writing direction. */}
          <span className="block ltr:rotate-180" aria-hidden>
            {collapsed ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        </button>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto" aria-label={t('nav.menu')}>
        {/*
          The back office, laid out as a menu rather than as a door. The home
          screen keeps its grid of cards — it is what a phone has instead of
          this — but on a desktop every section is one click from every other.
        */}
        {inPlatform && platform.data?.isPlatformAdmin && (
          <>
            {row({ ...PLATFORM_NAV, end: true })}
            {SECTIONS.map(({ id, icon }) =>
              row({ to: `/platform/${id}`, labelKey: `admin.sections.${id}`, icon }),
            )}

            <div className="pt-3 mt-2 border-t border-sidebar-border">
              <button
                type="button"
                onClick={() => setFrontOpen(!frontOpen)}
                aria-expanded={frontOpen}
                title={collapsed ? t('shell.frontMenu') : undefined}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
              >
                <ChevronDown
                  className={cn('h-4 w-4 shrink-0 transition-transform', !frontOpen && '-rotate-90 rtl:rotate-90')}
                />
                {!collapsed && (
                  <span className="text-xs font-semibold uppercase tracking-wide">{t('shell.frontMenu')}</span>
                )}
              </button>
            </div>
          </>
        )}

        {/* Hidden only where there is something else to look at: outside the
            back office this is the whole menu, and a rail folded to nothing is
            a rail nobody can unfold. */}
        {(!inPlatform || frontOpen) && (
        <>
        {PRIMARY_NAV.slice(0, PRIMARY_NAV_SPLIT).map(row)}

        <div className="relative">
          <button
            type="button"
            ref={plusRef}
            onClick={() => setOpenPlus((v) => !v)}
            aria-expanded={openPlus}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-sidebar-primary/10 text-sidebar-primary font-medium hover:bg-sidebar-primary/15 transition-colors"
            title={collapsed ? t('shell.new') : undefined}
          >
            <Plus className={cn('h-5 w-5 shrink-0 transition-transform', openPlus && 'rotate-45')} />
            {!collapsed && <span className="text-sm">{t('shell.new')}</span>}
          </button>
          {/* A portal: the rail's nav scrolls, so `overflow` clips it, and a
              menu three times the width of a collapsed rail was cut down to
              nothing. Anchored to the button, it is measured, not contained. */}
          <AnchoredMenu open={openPlus} onClose={() => setOpenPlus(false)} anchorRef={plusRef} width={224}>
            {CREATE_ACTIONS.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.to}
                  type="button"
                  onClick={() => {
                    setOpenPlus(false);
                    navigate(action.to);
                  }}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-4 py-3 text-sm text-foreground hover:bg-muted/60 transition-colors text-start',
                    index > 0 && 'border-t border-border',
                  )}
                >
                  <Icon className={cn('h-4 w-4', action.tone === 'muted' ? 'text-muted-foreground' : 'text-primary')} />
                  {t(action.labelKey)}
                </button>
              );
            })}
          </AnchoredMenu>
        </div>

        {/* The "+" sits third, between the two halves, because that is where
            the reference build puts it and where the bottom bar raises it. */}
        {PRIMARY_NAV.slice(PRIMARY_NAV_SPLIT).map(row)}
        </>
        )}

        {/*
          The one row the reference build does not have, and the only one that
          is not the same product the customer uses: the back office. It is
          drawn for Worki's own staff and nobody else — the card on the account
          screen stays for a phone, which has no rail to put this in. Inside the
          back office it is the heading above, not a row down here.
        */}
        {!inPlatform && platform.data?.isPlatformAdmin && row(PLATFORM_NAV)}
      </nav>

      <div className="border-t border-sidebar-border p-3 space-y-2">
        {!collapsed && (
          <Select
            value={i18n.language}
            aria-label={t('common.language')}
            onChange={(next) => {
              if (isLocale(next)) void changeLocale(next);
            }}
            className="h-9 px-2 bg-sidebar-accent/60 hover:bg-sidebar-accent text-sidebar-foreground"
          >
            {LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {LOCALE_LABELS[locale]}
              </option>
            ))}
          </Select>
        )}
        <button
          type="button"
          onClick={onLogout}
          title={collapsed ? t('common.logout') : undefined}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="text-sm">{t('common.logout')}</span>}
        </button>
      </div>
    </aside>
  );
}
